import { FC, useEffect, useMemo, useState } from 'react';
import { BiSolidCarCrash } from 'react-icons/bi';
import { BsCashCoin } from 'react-icons/bs';
import { FaCar, FaChartLine, FaFilter } from 'react-icons/fa';
import { MdInsertInvitation } from 'react-icons/md';
import { PiCar } from 'react-icons/pi';
import { RiMoneyDollarCircleFill } from 'react-icons/ri';
import { AdminTitle } from '../../components/AdminTitle';

import moment from 'moment-timezone';
import { DateRange } from 'rsuite/esm/DateRangePicker';
import { ChartPieRadialCard } from '../../components/DashBoardComponents/ChartPieRadialCard';
import ComposeBarLineChart from '../../components/DashBoardComponents/ComposeBarLineChart';
import DobleBarChart from '../../components/DashBoardComponents/DobleBarChart';
import SingleVerticalBarChart from '../../components/DashBoardComponents/SingleVerticalBarChart';
import StyledDateRangeSelect from '../../components/DashBoardComponents/StyledDateRangeSelect';
import TotalsInfoCard from '../../components/DashBoardComponents/TotalsInfoCard';
import ReactSelect from '../../components/ReactSelect';
import ReactSelectMulti from '../../components/ReactSelectMulti';
import { StyledButton } from '../../components/StyledButton/index';
import { useDashInfos, useDashInfosYearly } from '../../hooks/useDashInfos';
import theme from '../../styles/themes/default';
import { ufsNamed } from '../../utils/ufsNamed';
import {
  ChartsWrapper,
  DashboardContainer,
  InputsFilterWrapper,
  TotalsInfoCardWrapper,
} from './style';

interface IDataToFilter {
  startDate: Date | undefined;
  endDate: Date | undefined;
  uf: string | undefined;
  vehicleType: string | undefined;
}

const Dashboard: FC = () => {
  const [vehicleType, setVehicleType] = useState<string>('');
  const [ufsSelectedOptions, setUfsSelectedOptions] = useState<any>();

  const [selectedRangeDate, setSelectedRangeDate] = useState<DateRange | null>([
    moment.tz().subtract(3, 'month').startOf('day').toDate(),
    moment.tz().endOf('day').toDate(),
  ]);
  const [ufsSelectedOptionsToFilter, setUfsSelectedOptionsToFilter] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (ufsSelectedOptions) {
      let selectedOptions = '';
      ufsSelectedOptions.forEach((option: string) => {
        selectedOptions += `${option},`;
      });
      setUfsSelectedOptionsToFilter(selectedOptions || undefined);
    }
  }, [ufsSelectedOptions]);

  const [dataToFilter, setDataToFilter] = useState<IDataToFilter>({
    startDate: undefined,
    endDate: undefined,
    uf: undefined,
    vehicleType: undefined,
  });

  const { data, isLoading, isFetching, error, refetch } = useDashInfos({
    startDate: dataToFilter.startDate,
    endDate: dataToFilter.endDate,
    uf: dataToFilter.uf,
    vehicleType: dataToFilter.vehicleType,
  });
  useEffect(() => {
    refetch();
  }, [dataToFilter, refetch]);

  const dataDashYearly = useDashInfosYearly();

  const vehicleTypeOptions = [
    { value: 'CAMINHÃO', label: 'CAMINHÃO' },
    { value: 'CAMIONETA', label: 'CAMIONETA' },
    { value: 'MOTOCICLET', label: 'MOTOCICLETA' },
    { value: 'MICRO-ÔNIB', label: 'MICRO-ÔNIBUS' },
    { value: 'AUTOMÓVEL', label: 'AUTOMÓVEL' },
  ];

  const totalsInfoData = useMemo(
    () => [
      {
        icon: <BiSolidCarCrash />,
        titleCard: 'Total de infrações',
        percentFooter: '55',
        textFooter: 'que a ultima semana',
        totalValue: data?.finesCount?.count?.toString() || '00',
      },
      {
        icon: <RiMoneyDollarCircleFill />,
        titleCard: 'Valor Total de infrações',
        percentFooter: '55',
        textFooter: 'que a ultima semana',
        totalValue: `R$ ${(data?.finesCount?.value || 0)?.toLocaleString(
          'pt-BR',
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )}`,
      },
      {
        icon: <BsCashCoin />,
        titleCard: 'Valor Médio das infrações',
        percentFooter: '55',
        textFooter: 'que a ultima semana',
        totalValue: `R$ ${(data?.finesCount?.average || 0)?.toLocaleString(
          'pt-BR',
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )}`,
      },
      {
        icon: <FaChartLine />,
        titleCard: 'Débitos Veiculares a Pagar',
        percentFooter: '55',
        textFooter: 'que a ultima semana',
        totalValue: data?.vehicleDebts?.count.toString() || '00',
      },
      {
        icon: <MdInsertInvitation />,
        titleCard: 'Total de Débitos a Pagar',
        percentFooter: '55',
        textFooter: 'que a ultima semana',
        totalValue: `R$ ${(data?.vehicleDebts.value || 0)?.toLocaleString(
          'pt-BR',
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )}`,
      },
      {
        icon: <FaCar />,
        titleCard: 'Total de Veículos',
        percentFooter: '55',
        textFooter: 'que a ultima semana',
        totalValue: data?.totalUserVehicles?.toString() || '00',
      },
    ],
    [
      data?.finesCount?.average,
      data?.finesCount?.count,
      data?.finesCount?.value,
      data?.totalUserVehicles,
      data?.vehicleDebts?.count,
      data?.vehicleDebts.value,
    ],
  );

  const handleFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setDataToFilter({
      startDate: selectedRangeDate ? selectedRangeDate[0] : undefined,
      endDate: selectedRangeDate ? selectedRangeDate[1] : undefined,
      uf: ufsSelectedOptionsToFilter,
      vehicleType,
    });
  };

  return (
    <DashboardContainer>
      <AdminTitle title="Dashboard" icon={<PiCar />} />
      <InputsFilterWrapper onSubmit={handleFilters}>
        <div>
          <StyledDateRangeSelect
            placeholder="Selecionar período"
            setSelectedRangeDate={setSelectedRangeDate}
            value={selectedRangeDate}
          />
        </div>
        <div>
          <ReactSelectMulti
            placeholder={'UF'}
            setSelectedOptions={setUfsSelectedOptions}
            allOptions={ufsNamed.map(({ id }) => ({ value: id, label: id }))}
          />
        </div>
        {/* <div>
          <ReactSelect
            placeholder="Tipo de veículo"
            isLoading={isLoading}
            allOptions={vehicleTypeOptions}
            setSelectedOption={setVehicleType}
            name="licensing"
            isClearable
          />
        </div> */}

        <StyledButton
          icon={<FaFilter />}
          iconPosition="left"
          text={'Filtrar'}
          type="submit"
          style={{ minWidth: '164px' }}
        />
      </InputsFilterWrapper>

      <TotalsInfoCardWrapper>
        {totalsInfoData.map((data, index) => (
          <TotalsInfoCard
            isLoading={isLoading}
            key={index + data.titleCard}
            icon={data.icon}
            titleCard={data.titleCard}
            totalValue={data.totalValue}
            percentFooter={data.percentFooter}
            textFooter={data.textFooter}
          />
        ))}
      </TotalsInfoCardWrapper>

      <ChartsWrapper>
        <ComposeBarLineChart
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          title="Débitos por UF e Quantidade de Veículos"
          barColor={theme.colors.orange.primary}
          lineColor={theme.colors.blue.primary}
          data={data?.vehicleDataByUF?.map((each) => ({
            uf: each.uf,
            Veículos: each.vehicleCount,
            Débitos: each.value,
          }))}
        />

        <ChartPieRadialCard
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          title={'Quantidade de Veículos por UF'}
          data={
            data?.vehicleDataByUF?.map((each) => ({
              label: each.uf,
              value: each.vehicleCount,
            })) || []
          }
        />
        <ChartPieRadialCard
          data={
            data?.vehicleDebts
              ? [
                  {
                    label: 'Com Débitos',
                    value: data?.vehicleDebts?.countWithDebts || 0,
                  },
                  {
                    label: 'Sem Débitos',
                    value:
                      data?.vehicleDebts?.countWithoutDebts +
                        (data?.vehicleDebts?.countWithoutConsults || 0) || 0,
                  },
                ]
              : []
          }
          isLoading={isLoading}
          isFetching={isFetching}
          title={'Débitos Veículos'}
          error={error}
        />

        <SingleVerticalBarChart
          scale="linear"
          interval="preserveStartEnd"
          legendSize={136}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          title="Situação Licenciamento"
          barColor={theme.colors.orange.primary}
          data={
            data?.licensingStatusCount
              ? [
                  {
                    legend: 'Vencido',
                    value: data?.licensingStatusCount?.notLicensed,
                  },
                  {
                    legend: 'À Licenciar',
                    value: data?.licensingStatusCount?.availableForLicensing,
                  },
                  {
                    legend: 'Em dia',
                    value: data?.licensingStatusCount?.licensed,
                  },
                  {
                    legend: 'Desconhecido',
                    value: data?.licensingStatusCount?.undefined,
                  },
                ]
              : []
          }
        />
        {/* <SingleVerticalBarChart
          title="Top 10 Condutores (Multas)"
          barColor={theme.colors.blue.primary}
          data={Array.from({ length: 10 }).map((_, index) => ({
            legend: `Condutor-${index + 1}`,
            value: index + 1 * 2,
          }))}
        /> */}
        <SingleVerticalBarChart
          scale="linear"
          interval={1}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          title="Top 10 Veículos (Multas)"
          barColor={theme.colors.blue.primary}
          data={data?.vehicleRankingByFines?.slice(0, 10)?.map((each) => ({
            legend: each.placa,
            value: each.count,
            name: each.name,
          }))}
          legendSize={136}
        />
        <DobleBarChart
          isLoading={dataDashYearly.isLoading}
          isFetching={dataDashYearly.isFetching}
          error={error}
          title="Valor Total de Infrações Por Ano"
          barColorList={[
            theme.colors.orange.primary,
            theme.colors.blue.primary,
          ]}
          barColorListHover={[
            theme.colors.orange.light,
            theme.colors.blue.light,
          ]}
          data={dataDashYearly?.data?.valueByMonth || []}
        />
        {/* <DobleBarChart
          title="Quantidade de Infrações por Tipo"
          barColorList={[
            theme.colors.orange.primary,
            theme.colors.blue.primary,
          ]}
          barColorListHover={[
            theme.colors.orange.light,
            theme.colors.blue.light,
          ]}
          data={[7463, 7455, 6050, 5185, 5541, 6920].map((month) => ({
            each: month,
            2022: Math.floor(Math.random() * 1000),
            2023: Math.floor(Math.random() * 1000),
          }))}
          isLoading={false}
          isFetching={false}
          error={undefined}
        />
        <ChartPieRadialCard
          data={[
            { label: 'SP', value: 3 },
            { label: 'ES', value: 1 },
            { label: 'DF', value: 10 },
          ]}
          isLoading={isLoading}
          isFetching={isFetching}
          title={'Infrações mais Frequentes por Tipo'}
          error={error}
        /> */}
      </ChartsWrapper>
    </DashboardContainer>
  );
};

export default Dashboard;
